import type { TFunction } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { hasConditionValues, isLegacyCondition } from "./conditions";
import { LOGIC, VACANCY_OVERRIDE, type Logic } from "./constants";

import { OperatorValue } from "@/config/operatorConfig";
import type { SegmentField } from "@/features/segments/types";

const SEGMENT_NAME_MIN_LENGTH = 2;
export const SEGMENT_NAME_MAX_LENGTH = 100;
const CONTAINS_VALUE_MIN_LENGTH = 2;
const CONTAINS_VALUE_MAX_LENGTH = 100;

export const logicSchema = z.enum(LOGIC);
export const operatorSchema = z.enum(OperatorValue);
export const vacancyOverrideSchema = z.object({
  vacancyId: z.number(),
  type: z.enum(VACANCY_OVERRIDE),
});

const conditionValuesSchema = z.array(z.string());

export const hasLogic = <T extends { type?: Logic }>(
  value: T,
): value is T & { type: Logic } => value.type !== undefined;

const logicRequiredIssue = (t: TFunction) => ({
  message: t("errors.required"),
  path: ["type"],
});

const containsValueSchema = (t: TFunction) =>
  z
    .string()
    .trim()
    .min(
      CONTAINS_VALUE_MIN_LENGTH,
      t("admin.segments.builder.valueMin", { min: CONTAINS_VALUE_MIN_LENGTH }),
    )
    .max(
      CONTAINS_VALUE_MAX_LENGTH,
      t("admin.segments.builder.valueMax", { max: CONTAINS_VALUE_MAX_LENGTH }),
    );

export const segmentNameSchema = (t: TFunction) =>
  z
    .string()
    .trim()
    .min(1, { message: t("errors.required"), abort: true })
    .min(
      SEGMENT_NAME_MIN_LENGTH,
      t("admin.segments.builder.nameMin", { min: SEGMENT_NAME_MIN_LENGTH }),
    )
    .max(
      SEGMENT_NAME_MAX_LENGTH,
      t("admin.segments.builder.nameMax", { max: SEGMENT_NAME_MAX_LENGTH }),
    );

export const segmentCopyOptionsSchema = z.object({
  rules: z.boolean(),
  includedVacancies: z.boolean(),
  excludedVacancies: z.boolean(),
});

export type SegmentCopyOptions = z.infer<typeof segmentCopyOptionsSchema>;

export const createSegmentFormSchema = (t: TFunction, fields: SegmentField[]) =>
  z
    .object({
      name: segmentNameSchema(t),
      type: logicSchema.optional(),
      isInverted: z.boolean(),
      rules: z.array(
        z
          .object({
            clientKey: z.string(),
            id: z.string().optional(),
            index: z.number().optional(),
            name: z.string(),
            type: logicSchema.optional(),
            isInverted: z.boolean(),
            conditions: z
              .array(
                z
                  .object({
                    clientKey: z.string(),
                    id: z.string().optional(),
                    index: z.number().optional(),
                    isValueEdited: z.boolean().optional(),
                    field: z
                      .string()
                      .min(1, t("admin.segments.rules.conditions")),
                    operator: operatorSchema.optional(),
                    values: conditionValuesSchema,
                    isInverted: z.boolean(),
                  })
                  .superRefine((condition, ctx) => {
                    if (isLegacyCondition(condition, fields)) return;
                    if (!condition.operator) {
                      if (!condition.field) return;
                      ctx.addIssue({
                        code: "custom",
                        message: t("errors.required"),
                        path: ["operator"],
                      });
                      return;
                    }
                    if (condition.operator === OperatorValue.IS_NULL) return;
                    if (!hasConditionValues(condition.values)) {
                      ctx.addIssue({
                        code: "custom",
                        message: t("errors.required"),
                        path: ["values"],
                      });
                      return;
                    }
                    if (condition.operator !== OperatorValue.CONTAINS || !condition.isValueEdited) return;
                    const result = containsValueSchema(t).safeParse(condition.values[0]);
                    if (result.success) return;
                    for (const issue of result.error.issues) {
                      ctx.addIssue({ code: "custom", message: issue.message, path: ["values"] });
                    }
                  }),
              )
              .min(1, t("admin.segments.rules.condition")),
          })
          .refine((value): boolean => hasLogic(value), logicRequiredIssue(t)),
      ),
      vacancyOverrides: z.array(vacancyOverrideSchema),
    })
    .refine((value): boolean => hasLogic(value), logicRequiredIssue(t));

export type SegmentFormValues = z.infer<
  ReturnType<typeof createSegmentFormSchema>
>;

export type FormRule = SegmentFormValues["rules"][number];
export type FormCondition = FormRule["conditions"][number];

export const useSegmentFormSchema = (fields: SegmentField[]) => {
  const { t } = useTranslation();
  return useMemo(() => createSegmentFormSchema(t, fields), [t, fields]);
};

export const countPayloadSchema = z.object({
  type: logicSchema.optional(),
  isInverted: z.boolean(),
  rules: z.array(
    z.object({
      type: logicSchema.optional(),
      isInverted: z.boolean(),
      conditions: z.array(
        z.object({
          field: z.string(),
          operator: operatorSchema,
          values: conditionValuesSchema,
          isInverted: z.boolean(),
        }),
      ),
    }),
  ),
});

export type CountPayload = z.infer<typeof countPayloadSchema>;
export type CountRulePayload = CountPayload["rules"][number];

export const fieldOptionsResponseSchema = z.object({
  data: z.array(
    z.object({
      count: z.number(),
      label: z
        .string()
        .nullish()
        .transform((label) => label ?? undefined),
      value: z.union([z.string(), z.number()]),
    }),
  ),
  totalRecords: z.number(),
});

export const segmentFieldsResponseSchema = z.array(
  z.object({
    field: z.string(),
    defaultOperator: operatorSchema,
    operators: z.array(operatorSchema),
    labelKey: z.string(),
  }),
);

export type SegmentFieldsResponse = z.infer<typeof segmentFieldsResponseSchema>;

export const countResponseSchema = z.object({ count: z.number() });

export const savedSegmentSchema = z.object({ id: z.guid() });

export const saveErrorSchema = z.union([
  z.array(
    z.object({
      propertyName: z.string(),
      errorMessage: z.string(),
      errorCode: z.string().nullish(),
    }),
  ),
  z.object({ title: z.string() }),
]);
